import React, { useState } from "react";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import {
  MapPin,
  Building2,
  CalendarDays,
  Users,
  Plus,
  Minus,
  Search,
  Plane,
  Loader2,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { AIRPORTS, DESTINATIONS as MOCK_DEST, HOTELS as MOCK_HOTELS } from "../mock";
import { getQuote } from "../api";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { toast } from "sonner";

const TRIP_TABS = [
  { id: "round", labelKey: "roundTrip" },
  { id: "arrival", labelKey: "oneWayArrival" },
  { id: "departure", labelKey: "oneWayDeparture" },
  { id: "hth", labelKey: "hotelToHotel" },
];

const Field = ({ icon: Icon, label, children }) => (
  <div className="flex-1 min-w-[160px]">
    <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-teal-700 mb-1.5">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </label>
    {children}
  </div>
);

const DateField = ({ value, onChange, placeholder, minDate, locale }) => {
  const [open, setOpen] = useState(false);
  
  const handleDayClick = (day) => {
    if (day) {
      onChange(day);
      // Small delay to ensure state updates before closing
      setTimeout(() => setOpen(false), 50);
    }
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-11 px-3 rounded-lg border border-teal-100 bg-teal-50/40 text-left text-sm font-medium text-[#0d3b43] flex items-center justify-between hover:border-teal-300 transition-colors"
        >
          <span className={value ? "" : "text-slate-400"}>
            {value ? format(value, "dd MMM yyyy", { locale }) : placeholder}
          </span>
          <CalendarDays className="w-4 h-4 text-teal-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onDayClick={handleDayClick}
          disabled={(d) =>
            d < (minDate || new Date(new Date().setHours(0, 0, 0, 0)))
          }
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

const SearchWidget = ({ onSearch, destinations = MOCK_DEST, hotels = MOCK_HOTELS }) => {
  const { t, lang } = useLanguage();
  const locale = lang === "es" ? es : enUS;

  const [tripType, setTripType] = useState("round");
  const [origin, setOrigin] = useState("cun");
  const [destinationId, setDestinationId] = useState("");
  const [hotel, setHotel] = useState("");
  const [arrivalDate, setArrivalDate] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [passengers, setPassengers] = useState(2);
  const [loading, setLoading] = useState(false);

  const isHTH = tripType === "hth";
  const showDeparture = tripType === "round" || tripType === "departure";
  const showArrival = tripType !== "departure";
  const hotelOptions = destinationId ? hotels[destinationId] || [] : [];

  const handleSearch = async () => {
    const primaryDate = showArrival ? arrivalDate : departureDate;
    if (!destinationId || !primaryDate) {
      toast.error(t("fillFields"));
      return;
    }
    setLoading(true);
    try {
      const quote = await getQuote({ trip_type: tripType, destination_id: destinationId });
      onSearch({
        tripType,
        origin,
        destinationId,
        hotel,
        arrivalDate: arrivalDate ? format(arrivalDate, "yyyy-MM-dd") : null,
        departureDate: departureDate ? format(departureDate, "yyyy-MM-dd") : null,
        passengers,
        quote,
      });
      toast.success(t("searchDone"));
    } catch (e) {
      toast.error(t("bookingError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="buscador" className="w-full max-w-5xl mx-auto">
      <div className="flex flex-wrap gap-1 bg-[#0d3b43]/90 rounded-t-2xl p-1.5">
        {TRIP_TABS.map((tab) => {
          const active = tripType === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setTripType(tab.id)}
              className={`flex-1 min-w-[120px] px-3 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                active
                  ? "bg-white text-teal-700 shadow"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
            >
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-b-2xl rc-shadow-lg p-5 sm:p-6">
        <div className="flex flex-wrap gap-4">
          <Field icon={isHTH ? Building2 : Plane} label={t("origin")}>
            {isHTH ? (
              <Select value={origin === "cun" ? "" : origin} onValueChange={setOrigin}>
                <SelectTrigger className="h-11 bg-teal-50/40 border-teal-100">
                  <SelectValue placeholder={t("selectOrigin")} />
                </SelectTrigger>
                <SelectContent>
                  {destinations.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select value={origin} onValueChange={setOrigin}>
                <SelectTrigger className="h-11 bg-teal-50/40 border-teal-100">
                  <SelectValue placeholder={t("selectOrigin")} />
                </SelectTrigger>
                <SelectContent>
                  {AIRPORTS.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field icon={MapPin} label={t("destination")}>
            <Select
              value={destinationId}
              onValueChange={(v) => {
                setDestinationId(v);
                setHotel("");
              }}
            >
              <SelectTrigger className="h-11 bg-teal-50/40 border-teal-100">
                <SelectValue placeholder={t("selectDestination")} />
              </SelectTrigger>
              <SelectContent>
                {destinations.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field icon={Building2} label={t("hotel")}>
            <Select value={hotel} onValueChange={setHotel} disabled={!destinationId}>
              <SelectTrigger className="h-11 bg-teal-50/40 border-teal-100">
                <SelectValue placeholder={t("selectHotel")} />
              </SelectTrigger>
              <SelectContent>
                {hotelOptions.map((h) => (
                  <SelectItem key={h} value={h}>
                    {h}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="flex flex-wrap gap-4 mt-4">
          {showArrival && (
            <Field icon={CalendarDays} label={t("arrivalDate")}>
              <DateField
                value={arrivalDate}
                onChange={setArrivalDate}
                placeholder={t("pickDate")}
                locale={locale}
              />
            </Field>
          )}
          {showDeparture && (
            <Field icon={CalendarDays} label={t("departureDate")}>
              <DateField
                value={departureDate}
                onChange={setDepartureDate}
                placeholder={t("pickDate")}
                minDate={arrivalDate || undefined}
                locale={locale}
              />
            </Field>
          )}

          <Field icon={Users} label={t("passengers")}>
            <div className="h-11 px-2 rounded-lg border border-teal-100 bg-teal-50/40 flex items-center justify-between">
              <button
                onClick={() => setPassengers((p) => Math.max(1, p - 1))}
                className="w-8 h-8 rounded-lg bg-white border border-teal-100 flex items-center justify-center text-teal-700 hover:bg-teal-50"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="font-bold text-[#0d3b43] text-lg tabular-nums">
                {passengers}
              </span>
              <button
                onClick={() => setPassengers((p) => Math.min(14, p + 1))}
                className="w-8 h-8 rounded-lg bg-white border border-teal-100 flex items-center justify-center text-teal-700 hover:bg-teal-50"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </Field>

          <div className="flex items-end min-w-[180px] flex-1">
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-extrabold tracking-wide rounded-lg text-sm"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              {t("findTransfer")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchWidget;
